import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Alert,
    Badge,
    Button,
    Card,
    Col,
    Container,
    Form,
    ListGroup,
    Modal,
    OverlayTrigger,
    Row,
    Spinner,
    Table,
    Tooltip,
    Tab,
    Tabs,
} from "react-bootstrap";
import { Redirect } from "react-router-dom";
import SidebarPanelNavigation from "../../../components/SidebarPanelNavigation";
import LoadingIndicator from "../../../components/LoadingIndicator";
import TopProductsBarChart from "../../../components/TopProductsBarChart";
import useNotification from "../../../contexts/notification";
import CustomMessageModal from "../../../components/CustomMessageModal/";
import {
    enviarPromocoesParaLead,
    enviarPromocoesParaLeadsInteressados,
    enviarTemplateParaLead,
    enviarTemplateParaLeads,
    getLeads,
    enviarMensagemPersonalizada,
    getPromocoes,
    getAutomationSettings,
    updateAutomationSettings,
    consultarLimitesMensagensWhatsApp,
    consultarStatusFilaWhatsApp,
} from "../../../services/api";
import { parseISO, format, formatDistanceToNow } from "date-fns";
import ptBR from "date-fns/locale/pt-BR";
import useAuth from "../../../contexts/auth";

function formatRelativeDate(dateString) {
    if (!dateString) {
        return "Sem interações";
    }

    try {
        return formatDistanceToNow(parseISO(dateString), {
            addSuffix: true,
            locale: ptBR,
        });
    } catch (error) {
        return "Sem interações";
    }
}

function formatDateTime(dateString) {
    if (!dateString) {
        return "-";
    }

    try {
        return format(parseISO(dateString), "dd/MM/yyyy HH:mm");
    } catch (error) {
        return "-";
    }
}

export default function AutomationAdmin() {
    const { user } = useAuth();
    const isAdmin = user?.type === "admin";
    const isPoster = user?.type === "poster";
    const allowed = isAdmin || isPoster;

    const [loading, setLoading] = useState(true);
    const [leads, setLeads] = useState([]);
    const [promotions, setPromotions] = useState([]);
    const [automationEnabled, setAutomationEnabled] = useState(true);
    const [automationLoading, setAutomationLoading] = useState(true);
    const [automationUpdating, setAutomationUpdating] = useState(false);
    const [selectedLead, setSelectedLead] = useState("");
    const [logs, setLogs] = useState([]);
    const [pendingAction, setPendingAction] = useState(null);
    const [autoRefresh, setAutoRefresh] = useState(true);
    const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("interested");
    const [categoryFilter, setCategoryFilter] = useState("all");
    const [productFilter, setProductFilter] = useState("all");
    const [selectedLeadIds, setSelectedLeadIds] = useState([]);
    const [activeView, setActiveView] = useState("overview");
    const [bulkTemplateSending, setBulkTemplateSending] = useState(false);
    const [bulkPromotionSending, setBulkPromotionSending] = useState(false);
    const { setMessage } = useNotification();
    const requestInFlightRef = useRef(false);
    const selectAllRef = useRef(null);
    const [customMessageModal, setCustomMessageModal] = useState({
        open: false,
        leadIds: [],
        contextLabel: "",
    });
    const [customMessageSending, setCustomMessageSending] = useState(false);
    const [messagingLimits, setMessagingLimits] = useState([]);
    const [messagingLimitsLoading, setMessagingLimitsLoading] = useState(true);
    const [messagingLimitsError, setMessagingLimitsError] = useState(null);
    const [messagingLimitsFetchedAt, setMessagingLimitsFetchedAt] = useState(null);
    const [queueStatus, setQueueStatus] = useState(null);
    const [queueStatusLoading, setQueueStatusLoading] = useState(true);
    const [queueStatusError, setQueueStatusError] = useState(null);
    const [queueStatusFetchedAt, setQueueStatusFetchedAt] = useState(null);
    const [templateBatchSize, setTemplateBatchSize] = useState(1000);
    const fetchAutomationStatus = useCallback(() => {
        if (!allowed) {
            setAutomationLoading(false);
            return Promise.resolve();
        }

        setAutomationLoading(true);

        return getAutomationSettings()
            .then((response) => {
                const enabled = Boolean(response?.ai_responses_enabled);
                setAutomationEnabled(enabled);
            })
            .catch(() =>
                setMessage({
                    error: true,
                    content: "Não foi possível carregar o status da automação.",
                })
            )
            .finally(() => setAutomationLoading(false));
    }, [allowed, setMessage]);
    const fetchMessagingLimits = useCallback(() => {
        if (!allowed || !isAdmin) {
            setMessagingLimitsLoading(false);
            return Promise.resolve();
        }

        setMessagingLimitsLoading(true);
        setMessagingLimitsError(null);

        return consultarLimitesMensagensWhatsApp()
            .then((response) => {
                setMessagingLimits(response?.telefones || []);
                setMessagingLimitsFetchedAt(new Date().toISOString());
            })
            .catch(() => {
                const message = "Não foi possível consultar o limite de mensagens da Meta.";
                setMessagingLimitsError(message);
                setMessage({
                    error: true,
                    content: message,
                });
            })
            .finally(() => setMessagingLimitsLoading(false));
    }, [allowed, isAdmin, setMessage]);
    const fetchQueueStatus = useCallback(() => {
        if (!allowed) {
            setQueueStatusLoading(false);
            return Promise.resolve();
        }

        setQueueStatusLoading(true);
        setQueueStatusError(null);

        return consultarStatusFilaWhatsApp()
            .then((response) => {
                setQueueStatus(response || null);
                setQueueStatusFetchedAt(new Date().toISOString());
            })
            .catch(() => {
                const message = "Não foi possível consultar o status da fila.";
                setQueueStatusError(message);
                setMessage({
                    error: true,
                    content: message,
                });
            })
            .finally(() => setQueueStatusLoading(false));
    }, [allowed, setMessage]);
    const loadData = useCallback(
        (withLoader = true) => {
            if (!allowed) {
                if (withLoader) {
                    setLoading(false);
                }
                return Promise.resolve();
            }

            if (requestInFlightRef.current) {
                return Promise.resolve();
            }

            requestInFlightRef.current = true;

            if (withLoader) {
                setLoading(true);
            }

            return Promise.all([getLeads(), getPromocoes()])
                .then(([leadsResponse, promotionsResponse]) => {
                    setLeads(leadsResponse);
                    setPromotions(promotionsResponse);
                    setLastUpdatedAt(new Date().toISOString());
                })
                .catch(() =>
                    setMessage({
                        error: true,
                        content: "Não foi possível carregar os dados de automação.",
                    })
                )
                .finally(() => {
                    requestInFlightRef.current = false;
                    if (withLoader) {
                        setLoading(false);
                    }
                });
        },
        [allowed, setMessage]
    );

    useEffect(() => {
        loadData();
        fetchAutomationStatus();
        fetchMessagingLimits();
        fetchQueueStatus();
    }, [fetchAutomationStatus, fetchMessagingLimits, fetchQueueStatus, loadData]);

    useEffect(() => {
        if (!autoRefresh || !allowed) {
            return undefined;
        }

        const intervalId = setInterval(() => {
            loadData(false);
            fetchAutomationStatus();
            fetchMessagingLimits();
            fetchQueueStatus();
        }, 30000);

        return () => clearInterval(intervalId);
    }, [allowed, autoRefresh, fetchAutomationStatus, fetchMessagingLimits, fetchQueueStatus, loadData]);

    const interestedLeads = useMemo(
        () => leads.filter((lead) => lead.interesse && !lead.blacklist),
        [leads]
    );

    const onboardingLeads = useMemo(
        () => leads.filter((lead) => !lead.interesse && !lead.blacklist),
        [leads]
    );

    const blockedLeads = useMemo(
        () => leads.filter((lead) => lead.blacklist),
        [leads]
    );
    const leadById = useMemo(() => {
        const map = new Map();

        leads.forEach((lead) => {
            if (lead && lead.id !== undefined && lead.id !== null) {
                map.set(lead.id, lead);
            }
        });

        return map;
    }, [leads]);
    const selectedLeadData = useMemo(() => {
        if (!selectedLead) {
            return null;
        }

        return leads.find((lead) => lead.id === Number(selectedLead)) || null;
    }, [selectedLead, leads]);

    const productRequests = useMemo(() => {
        return leads.flatMap((lead) => {
            const requests = lead.productRequests || lead.product_requests || [];

            return requests.map((request) => ({
                ...request,
                lead,
                productName: request.productName || request.product_name || "",
                category: request.category || "Não categorizado",
                createdAt: request.createdAt || request.created_at || null,
            }));
        });
    }, [leads]);

    const totalProductRequests = productRequests.length;
    const leadsWithRequests = useMemo(() => {
        const ids = new Set();
        productRequests.forEach((request) => {
            if (request.lead?.id) {
                ids.add(request.lead.id);
            }
        });

        return ids.size;
    }, [productRequests]);

    const topProductStats = useMemo(() => {
        const stats = productRequests.reduce((accumulator, request) => {
            const rawName = (request.productName || request.product_name || "")
                .trim()
                .replace(/\s+/g, " ");

            if (!rawName) {
                return accumulator;
            }

            const key = rawName.toLowerCase();
            if (!accumulator[key]) {
                accumulator[key] = { product: rawName, count: 0 };
            }

            if (rawName.length > accumulator[key].product.length) {
                accumulator[key].product = rawName;
            }

            accumulator[key].count += 1;
            return accumulator;
        }, {});

        return Object.values(stats)
            .sort((a, b) => b.count - a.count)
            .slice(0, 10);
    }, [productRequests]);

    const recentRequests = useMemo(() => {
        return [...productRequests]
            .sort((a, b) => {
                const dateA = a.createdAt ? Date.parse(a.createdAt) : 0;
                const dateB = b.createdAt ? Date.parse(b.createdAt) : 0;
                return dateB - dateA;
            })
            .slice(0, 10);
    }, [productRequests]);

    const categorySummaries = useMemo(() => {
        const groupedByCategory = new Map();

        leads.forEach((lead) => {
            const leadRequests = (lead.productRequests || lead.product_requests || []).map(
                (request) => ({
                    id: request.id,
                    productName: request.productName || request.product_name || "",
                    category: (request.category || "Não categorizado").trim() || "Não categorizado",
                    createdAt: request.createdAt || request.created_at || null,
                })
            );

            leadRequests.forEach((request) => {
                if (!groupedByCategory.has(request.category)) {
                    groupedByCategory.set(request.category, new Map());
                }

                const categoryMap = groupedByCategory.get(request.category);
                const leadKey = lead.id || `${lead.phone || ""}-${lead.name || "lead"}`;

                if (!categoryMap.has(leadKey)) {
                    categoryMap.set(leadKey, {
                        id: lead.id || leadKey,
                        name: lead.name || "Lead",
                        phone: lead.phone || "",
                        products: [],
                        latestRequestAt: null,
                    });
                }

                const entry = categoryMap.get(leadKey);

                if (request.productName) {
                    entry.products.push(request.productName);
                }

                if (request.createdAt) {
                    const timestamp = Date.parse(request.createdAt);
                    if (!Number.isNaN(timestamp)) {
                        if (!entry.latestRequestAt || timestamp > entry.latestRequestAt) {
                            entry.latestRequestAt = timestamp;
                        }
                    }
                }
            });
        });

        return Array.from(groupedByCategory.entries())
            .map(([category, leadsMap]) => ({
                category,
                leads: Array.from(leadsMap.values())
                    .map((entry) => ({
                        ...entry,
                        products: Array.from(new Set(entry.products)),
                        latestRequestAt: entry.latestRequestAt
                            ? new Date(entry.latestRequestAt).toISOString()
                            : null,
                    }))
                    .sort((a, b) => a.name.localeCompare(b.name, "pt-BR", { sensitivity: "base" })),
            }))
            .sort((a, b) => {
                if (b.leads.length !== a.leads.length) {
                    return b.leads.length - a.leads.length;
                }

                return a.category.localeCompare(b.category, "pt-BR", { sensitivity: "base" });
            });
    }, [leads]);

    const leadPreferenceMap = useMemo(() => {
        const map = new Map();

        productRequests.forEach((request) => {
            const leadId = request.lead?.id;
            if (!leadId) {
                return;
            }

            if (!map.has(leadId)) {
                map.set(leadId, {
                    categories: new Set(),
                    products: new Set(),
                });
            }

            const entry = map.get(leadId);
            if (request.category) {
                entry.categories.add(request.category);
            }

            if (request.productName) {
                entry.products.add(request.productName);
            }
        });

        return map;
    }, [productRequests]);

    const categoryOptions = useMemo(() => {
        const categories = new Set();
        productRequests.forEach((request) => {
            if (request.category) {
                categories.add(request.category);
            }
        });
        return Array.from(categories).sort((a, b) =>
            a.localeCompare(b, "pt-BR", { sensitivity: "base" })
        );
    }, [productRequests]);

    const productOptions = useMemo(() => {
        const products = new Set();
        productRequests.forEach((request) => {
            const productName = request.productName?.trim();
            if (productName) {
                products.add(productName);
            }
        });
        return Array.from(products).sort((a, b) =>
            a.localeCompare(b, "pt-BR", { sensitivity: "base" })
        );
    }, [productRequests]);

    const filteredLeads = useMemo(() => {
        const normalizedSearch = search.trim().toLowerCase();

        return leads.filter((lead) => {
            const matchesSearch = normalizedSearch
                ? [lead.name, lead.phone]
                    .filter(Boolean)
                    .some((value) => value.toLowerCase().includes(normalizedSearch))
                : true;

            const matchesStatus = (() => {
                if (statusFilter === "all") {
                    return true;
                }

                if (statusFilter === "interested") {
                    return lead.interesse && !lead.blacklist;
                }

                if (statusFilter === "onboarding") {
                    return !lead.interesse && !lead.blacklist;
                }

                if (statusFilter === "blacklist") {
                    return Boolean(lead.blacklist);
                }

                return true;
            })();

            const preferenceEntry = leadPreferenceMap.get(lead.id);
            const matchesCategory =
                categoryFilter === "all"
                    ? true
                    : preferenceEntry?.categories?.has(categoryFilter) || false;

            const matchesProduct =
                productFilter === "all"
                    ? true
                    : preferenceEntry?.products?.has(productFilter) || false;

            return matchesSearch && matchesStatus && matchesCategory && matchesProduct;
        });
    }, [
        leads,
        search,
        statusFilter,
        categoryFilter,
        productFilter,
        leadPreferenceMap,
    ]);

    const filteredLeadIdSet = useMemo(() => {
        return new Set(filteredLeads.map((lead) => lead.id).filter(Boolean));
    }, [filteredLeads]);

    const selectedVisibleLeadIds = useMemo(() => {
        return selectedLeadIds.filter((id) => filteredLeadIdSet.has(id));
    }, [selectedLeadIds, filteredLeadIdSet]);
    const eligibleSelectedLeadIds = useMemo(() => {
        return selectedVisibleLeadIds.filter((id) => {
            const lead = leadById.get(id);
            return lead && !lead.blacklist;
        });
    }, [leadById, selectedVisibleLeadIds]);
    const allFilteredSelected =
        filteredLeadIdSet.size > 0 &&
        selectedVisibleLeadIds.length === filteredLeadIdSet.size;

    useEffect(() => {
        if (!selectAllRef.current) {
            return;
        }

        selectAllRef.current.indeterminate =
            selectedVisibleLeadIds.length > 0 && !allFilteredSelected;
    }, [selectedVisibleLeadIds.length, allFilteredSelected]);

    useEffect(() => {
        setSelectedLeadIds((prev) =>
            prev.filter((id) => leads.some((lead) => lead.id === id))
        );
    }, [leads]);

    const toggleLeadSelection = (leadId) => {
        setSelectedLeadIds((prev) => {
            const next = new Set(prev);
            if (next.has(leadId)) {
                next.delete(leadId);
            } else {
                next.add(leadId);
            }
            return Array.from(next);
        });
    };

    const handleToggleSelectAll = () => {
        if (filteredLeadIdSet.size === 0) {
            return;
        }

        setSelectedLeadIds((prev) => {
            const next = new Set(prev);
            const currentlySelected = Array.from(filteredLeadIdSet).filter((id) =>
                next.has(id)
            ).length;
            const shouldSelectAll = currentlySelected !== filteredLeadIdSet.size;

            if (shouldSelectAll) {
                filteredLeadIdSet.forEach((id) => next.add(id));
            } else {
                filteredLeadIdSet.forEach((id) => next.delete(id));
            }

            return Array.from(next);
        });
    };

    const handleClearSelection = () => {
        if (selectedVisibleLeadIds.length === 0) {
            return;
        }

        setSelectedLeadIds((prev) =>
            prev.filter((id) => !filteredLeadIdSet.has(id))
        );
    };

    const runBulkAction = async ({
        leadIds,
        action,
        setLoading: setBulkLoading,
        successMessage,
        errorMessage,
    }) => {
        if (leadIds.length === 0) {
            setMessage({
                error: true,
                content: "Selecione ao menos um lead da lista filtrada para continuar.",
            });
            return;
        }

        setBulkLoading(true);

        const failures = [];

        for (const id of leadIds) {
            try {
                // eslint-disable-next-line no-await-in-loop
                await action(id);
            } catch (error) {
                failures.push(id);
            }
        }

        if (failures.length === 0) {
            setMessage({
                error: false,
                content: successMessage(leadIds.length),
            });
        } else if (failures.length === leadIds.length) {
            setMessage({
                error: true,
                content: errorMessage,
            });
        } else {
            setMessage({
                error: true,
                content:
                    "Não foi possível concluir o envio para todos os leads selecionados. Tente novamente.",
            });
        }

        setBulkLoading(false);
    };

    const handleBulkSendTemplate = async () => {
        if (selectedVisibleLeadIds.length === 0) {
            setMessage({
                error: true,
                content: "Selecione ao menos um lead da lista filtrada para continuar.",
            });
            return;
        }

        setBulkTemplateSending(true);

        try {
            const shouldEnqueue = selectedVisibleLeadIds.length >= 50;
            const lote = Math.min(5000, Math.max(50, Number(templateBatchSize) || 500));
            setTemplateBatchSize(lote);
            const response = await enviarTemplateParaLeads({
                leadIds: selectedVisibleLeadIds,
                async: shouldEnqueue,
                lote,
            });

            const {
                total_enviados: totalEnviados = selectedVisibleLeadIds.length,
                total_falhas: totalFalhas = 0,
                total_lotes: totalLotes,
                processamento,
                total_leads: totalLeads,
            } = response || {};
            const leadsDestino = totalLeads || selectedVisibleLeadIds.length;
            const processadoNaFila = processamento?.tipo === "fila";

            if (processadoNaFila) {
                setMessage({
                    error: false,
                    content:
                        totalLotes && totalLotes > 1
                            ? `Templates enfileirados em ${totalLotes} lotes. O worker vai disparar para ${leadsDestino} leads sem travar o front-end.`
                            : `Templates enfileirados. O worker vai disparar para ${leadsDestino} lead${leadsDestino === 1 ? "" : "s"}.`,
                });
                return;
            }

            if (totalFalhas === 0) {
                setMessage({
                    error: false,
                    content: `${totalEnviados} template${totalEnviados === 1 ? "" : "s"} enviado${totalEnviados === 1 ? "" : "s"} com sucesso.`,
                });
            } else if (totalFalhas === selectedVisibleLeadIds.length) {
                setMessage({
                    error: true,
                    content: "Não foi possível enviar o template para os leads selecionados.",
                });
            } else {
                setMessage({
                    error: true,
                    content: "Não foi possível concluir o envio para todos os leads selecionados. Tente novamente.",
                });
            }
        } catch (error) {
            const apiMessage = error?.response?.data?.mensagem;
            setMessage({
                error: true,
                content: apiMessage || "Não foi possível enviar o template para os leads selecionados.",
            });
        } finally {
            setBulkTemplateSending(false);
        }
    };

    const handleBulkSendPromotions = () => {
        runBulkAction({
            leadIds: selectedVisibleLeadIds,
            action: enviarPromocoesParaLead,
            setLoading: setBulkPromotionSending,
            successMessage: (quantity) =>
                `${quantity} disparo${quantity === 1 ? "" : "s"} de promoção realizado${quantity === 1 ? "" : "s"
                } com sucesso.`,
            errorMessage:
                "Não foi possível disparar as promoções para os leads selecionados.",
        });
    };
    const handleCloseCustomMessageModal = () => {
        if (customMessageSending) {
            return;
        }

        setCustomMessageModal({
            open: false,
            leadIds: [],
            contextLabel: "",
        });
    };

    const openCustomMessageModalForLead = (lead) => {
        if (!lead || !lead.id) {
            return;
        }

        if (lead.blacklist) {
            setMessage({
                error: true,
                content: "Este lead está na blacklist. Remova-o para enviar mensagens.",
            });
            return;
        }

        setCustomMessageModal({
            open: true,
            leadIds: [lead.id],
            contextLabel: lead.name || lead.phone || "Lead selecionado",
        });
    };

    const handleOpenBulkCustomMessageModal = () => {
        if (eligibleSelectedLeadIds.length === 0) {
            setMessage({
                error: true,
                content:
                    "Selecione ao menos um lead liberado (fora da blacklist) para enviar mensagens personalizadas.",
            });
            return;
        }

        const label =
            eligibleSelectedLeadIds.length === 1
                ? leadById.get(eligibleSelectedLeadIds[0])?.name ||
                leadById.get(eligibleSelectedLeadIds[0])?.phone ||
                "Lead selecionado"
                : `${eligibleSelectedLeadIds.length} leads selecionados`;

        setCustomMessageModal({
            open: true,
            leadIds: eligibleSelectedLeadIds,
            contextLabel: label,
        });
    };

    const handleSubmitCustomMessage = async (text) => {
        const trimmed = (text || "").trim();

        if (trimmed.length < 3 || customMessageModal.leadIds.length === 0) {
            setMessage({
                error: true,
                content: "Informe uma mensagem com ao menos 3 caracteres.",
            });
            return;
        }

        setCustomMessageSending(true);

        try {
            const response = await enviarMensagemPersonalizada({
                leadIds: customMessageModal.leadIds,
                mensagem: trimmed,
            });

            const totalEnviados =
                response?.total_enviados ?? customMessageModal.leadIds.length;
            const totalFalhas = response?.total_falhas ?? 0;

            const falhasDetalhadas = Array.isArray(response?.falhas)
                ? response.falhas.slice(0, 3)
                : [];

            const detalhesFalha = falhasDetalhadas
                .map((falha) => `#${falha?.lead_id}: ${falha?.motivo}`)
                .join(" | ");

            const suffix = detalhesFalha
                ? ` Detalhes: ${detalhesFalha}${(response?.falhas?.length || 0) > falhasDetalhadas.length ? "..." : ""
                }`
                : "";

            const content =
                totalFalhas > 0
                    ? `${totalEnviados} mensagem${totalEnviados === 1 ? "" : "s"} enviada${totalEnviados === 1 ? "" : "s"}, ${totalFalhas} falha${totalFalhas === 1 ? "" : "s"} no envio.${suffix}`
                    : totalEnviados === 1
                        ? "Mensagem personalizada enviada com sucesso."
                        : `${totalEnviados} mensagens personalizadas enviadas com sucesso.`;

            setMessage({
                error: totalFalhas > 0,
                content,
            });

            setCustomMessageModal({
                open: false,
                leadIds: [],
                contextLabel: "",
            });

            await loadData(false);
        } catch (error) {
            const apiMessage =
                error?.response?.data?.mensagem ||
                error?.response?.data?.message ||
                "Não foi possível enviar a mensagem personalizada. Tente novamente.";
            setMessage({
                error: true,
                content: apiMessage,
            });
        } finally {
            setCustomMessageSending(false);
        }
    };

    const productInterestSummaries = useMemo(() => {
        const groupedByProduct = new Map();

        leads.forEach((lead) => {
            const leadRequests = (lead.productRequests || lead.product_requests || []).map(
                (request) => ({
                    id: request.id,
                    productName: (request.productName || request.product_name || "").trim(),
                    category: (request.category || "Não categorizado").trim() || "Não categorizado",
                    createdAt: request.createdAt || request.created_at || null,
                })
            );

            leadRequests.forEach((request) => {
                if (!request.productName) {
                    return;
                }

                const normalizedProduct = request.productName.toLowerCase();
                if (!groupedByProduct.has(normalizedProduct)) {
                    groupedByProduct.set(normalizedProduct, {
                        productName: request.productName,
                        categories: new Set(),
                        latestRequestAt: null,
                        leads: new Map(),
                    });
                }

                const productEntry = groupedByProduct.get(normalizedProduct);

                if (request.productName.length > productEntry.productName.length) {
                    productEntry.productName = request.productName;
                }

                productEntry.categories.add(request.category);

                const leadKey = lead.id || `${lead.phone || ""}-${lead.name || "lead"}`;
                if (!productEntry.leads.has(leadKey)) {
                    productEntry.leads.set(leadKey, {
                        id: lead.id || leadKey,
                        name: lead.name || "Lead",
                        phone: lead.phone || "",
                        categories: new Set(),
                        latestRequestAt: null,
                    });
                }

                const leadEntry = productEntry.leads.get(leadKey);
                leadEntry.categories.add(request.category);

                if (request.createdAt) {
                    const timestamp = Date.parse(request.createdAt);
                    if (!Number.isNaN(timestamp)) {
                        if (!leadEntry.latestRequestAt || timestamp > leadEntry.latestRequestAt) {
                            leadEntry.latestRequestAt = timestamp;
                        }

                        if (!productEntry.latestRequestAt || timestamp > productEntry.latestRequestAt) {
                            productEntry.latestRequestAt = timestamp;
                        }
                    }
                }
            });
        });

        return Array.from(groupedByProduct.values())
            .map((entry) => ({
                productName: entry.productName,
                categories: Array.from(entry.categories).sort((a, b) =>
                    a.localeCompare(b, "pt-BR", { sensitivity: "base" })
                ),
                latestRequestAt: entry.latestRequestAt
                    ? new Date(entry.latestRequestAt).toISOString()
                    : null,
                leads: Array.from(entry.leads.values())
                    .map((leadEntry) => ({
                        ...leadEntry,
                        categories: Array.from(leadEntry.categories).sort((a, b) =>
                            a.localeCompare(b, "pt-BR", { sensitivity: "base" })
                        ),
                        latestRequestAt: leadEntry.latestRequestAt
                            ? new Date(leadEntry.latestRequestAt).toISOString()
                            : null,
                    }))
                    .sort((a, b) => a.name.localeCompare(b.name, "pt-BR", { sensitivity: "base" })),
            }))
            .sort((a, b) => {
                if (b.leads.length !== a.leads.length) {
                    return b.leads.length - a.leads.length;
                }

                return a.productName.localeCompare(b.productName, "pt-BR", { sensitivity: "base" });
            });
    }, [leads]);

    const pushLog = (message, variant = "info") => {
        setLogs((prevLogs) => {
            const nextLogs = [
                { id: Date.now(), message, variant },
                ...prevLogs,
            ].slice(0, 6);
            return nextLogs;
        });
    };

    const handleToggleAutomation = () => {
        if (!isAdmin || automationUpdating) {
            return;
        }

        const nextStatus = !automationEnabled;
        setAutomationUpdating(true);

        updateAutomationSettings({ ai_responses_enabled: nextStatus })
            .then((response) => {
                const enabled = Boolean(response?.ai_responses_enabled);
                setAutomationEnabled(enabled);
                setMessage({
                    error: false,
                    content: enabled
                        ? "Automação reativada com sucesso."
                        : "Automação pausada. Nenhuma resposta automática será enviada.",
                });
            })
            .catch(() =>
                setMessage({
                    error: true,
                    content: "Não foi possível atualizar o status da automação.",
                })
            )
            .finally(() => setAutomationUpdating(false));
    };

    const handleAction = async (actionName, callback, options = {}) => {
        setPendingAction(actionName);
        try {
            const response = await callback();
            const successMessage =
                response?.status || "Fluxo executado com sucesso.";
            setMessage({ error: false, content: successMessage });
            pushLog(successMessage, "success");
            if (!options.skipRefresh) {
                await loadData(false);
            }
        } catch (error) {
            const errorMessage =
                error?.response?.data?.mensagem ||
                "Não foi possível completar a ação. Tente novamente.";
            setMessage({ error: true, content: errorMessage });
            pushLog(errorMessage, "danger");
        } finally {
            setPendingAction(null);
        }
    };

    const totalPromotions = promotions.length;
    const automationCoverage = leads.length
        ? Math.round((interestedLeads.length / leads.length) * 100)
        : 0;
    const lastUpdatedDescription = lastUpdatedAt
        ? formatDistanceToNow(parseISO(lastUpdatedAt), {
            addSuffix: true,
            locale: ptBR,
        })
        : "-";

    const messagingLimitLabels = {
        TIER_250: "250 conversas/24h",
        TIER_1000: "1.000 conversas/24h",
        TIER_10000: "10.000 conversas/24h",
        TIER_100000: "100.000 conversas/24h",
        TIER_250000: "250.000 conversas/24h",
        TIER_500000: "500.000 conversas/24h",
    };

    const qualityLabels = {
        GREEN: { text: "Boa", variant: "success" },
        YELLOW: { text: "Mediana", variant: "warning" },
        RED: { text: "Crítica", variant: "danger" },
    };

    const formatMessagingLimit = (tier) => messagingLimitLabels[tier] || tier || "Não disponível";

    const qualityTag = (quality) => qualityLabels[quality] || { text: quality || "-", variant: "secondary" };

    const selectedCount = selectedVisibleLeadIds.length;
    const eligibleSelectedCount = eligibleSelectedLeadIds.length;

    if (!allowed) {
        return <Redirect to="/" />;
    }

    return (
        <>
            <Container fluid>
                <div>
                    <SidebarPanelNavigation isAdmin={isAdmin} />
                    <div className="content-panel">
                        {loading ? (
                            <LoadingIndicator />
                        ) : (
                            <>
                                <Row className="align-items-center">
                                    <Col md={8} className="mb-3">
                                        <h2 className="mb-1">Automação WhatsApp</h2>
                                        <p className="text-muted mb-0">
                                            Controle o envio de templates, respostas automáticas e promoções direcionadas em um único painel.
                                        </p>
                                    </Col>
                                    <Col
                                        md={4}
                                        className="mb-3 d-flex flex-column align-items-md-end align-items-start"
                                    >
                                        <OverlayTrigger
                                            placement="bottom"
                                            overlay={
                                                <Tooltip id="automation-refresh-tooltip">
                                                    Recarrega leads e promoções automaticamente a cada 30 segundos.
                                                </Tooltip>
                                            }
                                        >
                                            <Form.Check
                                                type="switch"
                                                id="automation-auto-refresh"
                                                label="Atualização automática"
                                                checked={autoRefresh}
                                                onChange={(event) => setAutoRefresh(event.target.checked)}
                                                className="mb-2"
                                            />
                                        </OverlayTrigger>
                                        <div className="d-flex align-items-center">
                                            <Button
                                                variant="outline-primary"
                                                onClick={() =>
                                                    handleAction("sync", () => loadData(), { skipRefresh: true })
                                                }
                                                disabled={pendingAction === "sync"}
                                                className="mr-2"
                                            >
                                                {pendingAction === "sync" ? "Sincronizando..." : "Atualizar dados"}
                                            </Button>
                                            <small className="text-muted">
                                                Atualizado {lastUpdatedDescription}
                                            </small>
                                        </div>
                                    </Col>
                                </Row>

                                <Card className="mb-3">
                                    <Card.Body className="d-flex flex-column flex-md-row justify-content-between align-items-md-center">
                                        <div className="mb-3 mb-md-0">
                                            <Card.Title className="mb-1">Respostas automáticas</Card.Title>
                                            <Card.Subtitle className="text-muted">
                                                Pausar impede que o robô envie mensagens sem revisão. Você pode reativar a qualquer momento.
                                            </Card.Subtitle>
                                        </div>
                                        <div className="d-flex flex-column align-items-md-end align-items-start">
                                            <div className="d-flex align-items-center mb-1">
                                                <Badge variant={automationEnabled ? "success" : "secondary"} className="mr-2">
                                                    {automationEnabled ? "Ativa" : "Pausada"}
                                                </Badge>
                                                <Form.Check
                                                    type="switch"
                                                    id="automation-enabled-toggle"
                                                    label={automationEnabled ? "Desativar automação" : "Ativar automação"}
                                                    checked={automationEnabled}
                                                    onChange={handleToggleAutomation}
                                                    disabled={!isAdmin || automationLoading || automationUpdating}
                                                />
                                            </div>
                                            <small className="text-muted">
                                                {automationLoading
                                                    ? "Carregando status..."
                                                    : isAdmin
                                                        ? "Apenas administradores podem alterar este controle."
                                                        : "Somente administradores podem editar este controle."}
                                            </small>
                                        </div>
                                    </Card.Body>
                                </Card>

                                <Card className="mb-3">
                                    <Card.Body>
                                        <div className="d-flex justify-content-between align-items-start flex-wrap">
                                            <div>
                                                <Card.Title className="mb-1">Status da fila de WhatsApp</Card.Title>
                                                <Card.Subtitle className="text-muted">
                                                    Mostra se o worker está vivo e pronto para disparos massivos.
                                                </Card.Subtitle>
                                            </div>
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                onClick={() => fetchQueueStatus()}
                                                disabled={queueStatusLoading}
                                            >
                                                {queueStatusLoading ? "Atualizando..." : "Atualizar"}
                                            </Button>
                                        </div>

                                        <div className="mt-3">
                                            {queueStatusLoading ? (
                                                <div className="d-flex align-items-center text-muted">
                                                    <Spinner animation="border" size="sm" className="mr-2" />
                                                    Consultando status da fila...
                                                </div>
                                            ) : queueStatusError ? (
                                                <Alert variant="light" className="mb-0">
                                                    {queueStatusError}
                                                </Alert>
                                            ) : !queueStatus ? (
                                                <Alert variant="light" className="mb-0">
                                                    Nenhuma informação de fila retornada.
                                                </Alert>
                                            ) : (
                                                <div>
                                                    <div className="d-flex flex-wrap align-items-center mb-3">
                                                        <Badge
                                                            variant={queueStatus.fila_pronta_para_100k ? "success" : "warning"}
                                                            className="mr-2 mb-2"
                                                        >
                                                            {queueStatus.fila_pronta_para_100k
                                                                ? "Fila pronta para 100k"
                                                                : "Fila não pronta para 100k"}
                                                        </Badge>
                                                        <Badge variant="light" className="mr-2 mb-2">
                                                            Driver: {queueStatus.driver || "desconhecido"}
                                                        </Badge>
                                                        <Badge
                                                            variant={queueStatus.worker_ativo_recentemente ? "success" : "warning"}
                                                            className="mr-2 mb-2"
                                                        >
                                                            Worker {queueStatus.worker_ativo_recentemente ? "ativo" : "inativo"}
                                                        </Badge>
                                                    </div>

                                                    <div className="text-muted">
                                                        <div>
                                                            Último heartbeat: {queueStatus.ultimo_heartbeat
                                                                ? formatRelativeDate(queueStatus.ultimo_heartbeat)
                                                                : "nenhum registrado"}
                                                        </div>
                                                        <div>
                                                            Fila pendente: {queueStatus.fila_whatsapp_pendente ?? "não disponível"}
                                                        </div>
                                                        {queueStatus.mensagem_curta && (
                                                            <div className="text-danger">{queueStatus.mensagem_curta}</div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}

                                            {queueStatusFetchedAt && !queueStatusLoading && (
                                                <small className="text-muted d-block mt-2">
                                                    Atualizado {formatRelativeDate(queueStatusFetchedAt)}.
                                                </small>
                                            )}
                                        </div>
                                    </Card.Body>
                                </Card>

                                <Card className="mb-3">
                                    <Card.Body>
                                        <div className="d-flex justify-content-between align-items-start flex-wrap">
                                            <div>
                                                <Card.Title className="mb-1">Limite de envios na Meta</Card.Title>
                                                <Card.Subtitle className="text-muted">
                                                    Consulta direta à API do WhatsApp Business para acompanhar limite e qualidade do número.
                                                </Card.Subtitle>
                                            </div>
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                onClick={() => fetchMessagingLimits()}
                                                disabled={messagingLimitsLoading}
                                            >
                                                {messagingLimitsLoading ? "Atualizando..." : "Atualizar"}
                                            </Button>
                                        </div>

                                        <div className="mt-3">
                                            {messagingLimitsLoading ? (
                                                <div className="d-flex align-items-center text-muted">
                                                    <Spinner animation="border" size="sm" className="mr-2" />
                                                    Consultando limite diretamente na Meta...
                                                </div>
                                            ) : messagingLimitsError ? (
                                                <Alert variant="light" className="mb-0">
                                                    {messagingLimitsError}
                                                </Alert>
                                            ) : messagingLimits.length === 0 ? (
                                                <Alert variant="light" className="mb-0">
                                                    Nenhum número retornado pela API da Meta.
                                                </Alert>
                                            ) : (
                                                <ListGroup variant="flush">
                                                    {messagingLimits.map((phone) => {
                                                        const quality = qualityTag(phone.qualidade);
                                                        const limitDescription =
                                                            phone.limite_mensagens_descricao ||
                                                            formatMessagingLimit(phone.limite_mensagens);
                                                        const windowDescription = phone.janela_horas
                                                            ? ` (janela de ${phone.janela_horas}h)`
                                                            : "";

                                                        return (
                                                            <ListGroup.Item key={phone.id || phone.numero}>
                                                                <div className="d-flex justify-content-between align-items-center flex-wrap">
                                                                    <div className="mr-3">
                                                                        <strong>{phone.numero || "Número não informado"}</strong>
                                                                        <div className="text-muted small">
                                                                            {phone.nome_verificado || "Nome não verificado"}
                                                                        </div>
                                                                        <div className="text-muted small">
                                                                            Limite atual: {limitDescription}
                                                                            {windowDescription}
                                                                        </div>
                                                                    </div>
                                                                    <div className="d-flex align-items-center flex-wrap">
                                                                        <Badge variant="light" className="mr-2 mb-1">
                                                                            {limitDescription}
                                                                        </Badge>
                                                                        <Badge variant={quality.variant} className="mr-2 mb-1">
                                                                            Qualidade: {quality.text}
                                                                        </Badge>
                                                                        {phone.modo_conta && (
                                                                            <Badge variant="secondary" className="mr-2 mb-1">
                                                                                Modo: {phone.modo_conta}
                                                                            </Badge>
                                                                        )}
                                                                        {phone.status_verificacao && (
                                                                            <Badge variant="secondary" className="mr-2 mb-1">
                                                                                Verificação: {phone.status_verificacao}
                                                                            </Badge>
                                                                        )}
                                                                        {phone.status_nome && (
                                                                            <Badge variant="secondary" className="mr-2 mb-1">
                                                                                Nome: {phone.status_nome}
                                                                            </Badge>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </ListGroup.Item>
                                                        );
                                                    })}
                                                </ListGroup>
                                            )}

                                            {messagingLimitsFetchedAt && !messagingLimitsLoading && (
                                                <small className="text-muted d-block mt-2">
                                                    Atualizado {formatRelativeDate(messagingLimitsFetchedAt)}.
                                                </small>
                                            )}
                                        </div>
                                    </Card.Body>
                                </Card>

                                <Tabs
                                    id="automation-submenu"
                                    activeKey={activeView}
                                    onSelect={(key) => setActiveView(key || "overview")}
                                    className="mb-4 automation-submenu"
                                    justify
                                >
                                    <Tab eventKey="overview" title="Painel">
                                        <div className="pt-4">
                                            <Row className="my-3">
                                                <Col xl={4} md={6} className="mb-3">
                                                    <Card>
                                                        <Card.Body>
                                                            <h5 className="text-muted">Leads ativos</h5>
                                                            <div className="d-flex align-items-baseline justify-content-between">
                                                                <h2 className="text-success mb-0">{interestedLeads.length}</h2>
                                                                <Badge variant="light">{automationCoverage}% da base engajada</Badge>
                                                            </div>
                                                            <small className="text-muted d-block mt-2">
                                                                Leads que responderam "Continuar recebendo" e estão aptos a receber mensagens dentro da janela de 24h.
                                                            </small>
                                                        </Card.Body>
                                                    </Card>
                                                </Col>
                                                <Col xl={4} md={6} className="mb-3">
                                                    <Card>
                                                        <Card.Body>
                                                            <h5 className="text-muted">Novos leads em onboarding</h5>
                                                            <div className="d-flex align-items-baseline justify-content-between">
                                                                <h2 className="text-primary mb-0">{onboardingLeads.length}</h2>
                                                                <Badge variant="light">Aguardando resposta</Badge>
                                                            </div>
                                                            <small className="text-muted d-block mt-2">
                                                                Leads que receberam o template mas ainda não interagiram ou não confirmaram interesse.
                                                            </small>
                                                        </Card.Body>
                                                    </Card>
                                                </Col>
                                                <Col xl={4} md={6} className="mb-3">
                                                    <Card>
                                                        <Card.Body>
                                                            <h5 className="text-muted">Blacklist</h5>
                                                            <div className="d-flex align-items-baseline justify-content-between">
                                                                <h2 className="text-danger mb-0">{blockedLeads.length}</h2>
                                                                <Badge variant="light">Opt-out</Badge>
                                                            </div>
                                                            <small className="text-muted d-block mt-2">
                                                                Leads que solicitaram parar de receber comunicações. Eles ficam protegidos de novos disparos.
                                                            </small>
                                                        </Card.Body>
                                                    </Card>
                                                </Col>
                                            </Row>

                                            <Row className="mb-3">
                                                <Col xl={6} md={6} className="mb-3">
                                                    <Card className="h-100">
                                                        <Card.Body>
                                                            <h5 className="text-muted">Solicitações de produtos</h5>
                                                            <div className="d-flex align-items-baseline justify-content-between">
                                                                <h2 className="text-dark mb-0">{totalProductRequests}</h2>
                                                                <Badge variant="light">
                                                                    {leadsWithRequests} lead{leadsWithRequests === 1 ? "" : "s"} únicos
                                                                </Badge>
                                                            </div>
                                                            <small className="text-muted d-block mt-2">
                                                                Pedidos recebidos pelo formulário “Pedir Produto” ou pelas interações automáticas recentes.
                                                            </small>
                                                        </Card.Body>
                                                    </Card>
                                                </Col>
                                                <Col xl={6} md={6} className="mb-3">
                                                    <Card className="h-100">
                                                        <Card.Body>
                                                            <h5 className="text-muted">Produtos com maior interesse</h5>
                                                            {topProductStats.length === 0 ? (
                                                                <Alert variant="light" className="mb-0">
                                                                    Ainda não há produtos com interesse registrado. Assim que os leads informarem seus pedidos, eles aparecerão aqui.
                                                                </Alert>
                                                            ) : (
                                                                <TopProductsBarChart products={topProductStats} />
                                                            )}
                                                        </Card.Body>
                                                    </Card>
                                                </Col>
                                            </Row>

                                            <Row className="mb-3">
                                                <Col xl={6} className="mb-3">
                                                    <Card className="h-100">
                                                        <Card.Body>
                                                            <Card.Title>Respostas por categoria</Card.Title>
                                                            <Card.Subtitle className="text-muted mb-3">
                                                                Visualize rapidamente quem solicitou cada tipo de produto.
                                                            </Card.Subtitle>
                                                            {categorySummaries.length === 0 ? (
                                                                <Alert variant="light" className="mb-0">
                                                                    Nenhuma solicitação categorizada até o momento.
                                                                </Alert>
                                                            ) : (
                                                                <div>
                                                                    {categorySummaries.map((group) => (
                                                                        <div key={group.category} className="mb-4">
                                                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                                                <h6 className="mb-0">{group.category}</h6>
                                                                                <Badge variant="primary">
                                                                                    {group.leads.length} resposta{group.leads.length === 1 ? "" : "s"}
                                                                                </Badge>
                                                                            </div>
                                                                            <Table size="sm" responsive className="mb-0">
                                                                                <thead>
                                                                                    <tr>
                                                                                        <th>Nome</th>
                                                                                        <th>Produtos solicitados</th>
                                                                                        <th>Última solicitação</th>
                                                                                    </tr>
                                                                                </thead>
                                                                                <tbody>
                                                                                    {group.leads.map((lead) => (
                                                                                        <tr key={`${group.category}-${lead.id}`}>
                                                                                            <td className="align-middle">{lead.name}</td>
                                                                                            <td className="align-middle" style={{ minWidth: 180 }}>
                                                                                                {lead.products.length > 0 ? lead.products.join(", ") : "-"}
                                                                                            </td>
                                                                                            <td className="align-middle">
                                                                                                {lead.latestRequestAt ? formatDateTime(lead.latestRequestAt) : "-"}
                                                                                            </td>
                                                                                        </tr>
                                                                                    ))}
                                                                                </tbody>
                                                                            </Table>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </Card.Body>
                                                    </Card>
                                                </Col>
                                                <Col xl={6} className="mb-3">
                                                    <Card className="h-100">
                                                        <Card.Body>
                                                            <Card.Title>Produtos com interessados</Card.Title>
                                                            <Card.Subtitle className="text-muted mb-3">
                                                                Agrupamos os leads pelos itens solicitados para facilitar o contato direcionado.
                                                            </Card.Subtitle>
                                                            {productInterestSummaries.length === 0 ? (
                                                                <Alert variant="light" className="mb-0">
                                                                    Ainda não há produtos solicitados pelos leads.
                                                                </Alert>
                                                            ) : (
                                                                <div>
                                                                    {productInterestSummaries.map((group) => (
                                                                        <div key={group.productName} className="mb-4">
                                                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                                                                <h6 className="mb-0">{group.productName}</h6>
                                                                                <Badge variant={group.leads.length > 1 ? "success" : "secondary"}>
                                                                                    {group.leads.length} interessado{group.leads.length === 1 ? "" : "s"}
                                                                                </Badge>
                                                                            </div>
                                                                            {group.categories.length > 0 && (
                                                                                <small className="d-block text-muted mb-2">
                                                                                    Categorias marcadas: {group.categories.join(", ")}
                                                                                </small>
                                                                            )}
                                                                            <Table size="sm" responsive className="mb-0">
                                                                                <thead>
                                                                                    <tr>
                                                                                        <th>Nome</th>
                                                                                        <th>Categorias informadas</th>
                                                                                        <th>Última solicitação</th>
                                                                                    </tr>
                                                                                </thead>
                                                                                <tbody>
                                                                                    {group.leads.map((lead) => (
                                                                                        <tr key={`${group.productName}-${lead.id}`}>
                                                                                            <td className="align-middle">{lead.name}</td>
                                                                                            <td className="align-middle" style={{ minWidth: 180 }}>
                                                                                                {lead.categories.length > 0 ? lead.categories.join(", ") : "-"}
                                                                                            </td>
                                                                                            <td className="align-middle">
                                                                                                {lead.latestRequestAt ? formatDateTime(lead.latestRequestAt) : "-"}
                                                                                            </td>
                                                                                        </tr>
                                                                                    ))}
                                                                                </tbody>
                                                                            </Table>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </Card.Body>
                                                    </Card>
                                                </Col>
                                            </Row>

                                            <Row>
                                                <Col lg={6} className="mb-3">
                                                    <Card className="h-100">
                                                        <Card.Body>
                                                            <Card.Title>Fluxo automatizado</Card.Title>
                                                            <Card.Subtitle className="text-muted mb-3">
                                                                Visualize o comportamento programado para cada interação.
                                                            </Card.Subtitle>
                                                            <ListGroup variant="flush" className="flow-preview">
                                                                <ListGroup.Item>
                                                                    <strong>1. Template inicial</strong>
                                                                    <p className="mb-0 text-muted">
                                                                        Dispara o template <em>confirmar_preferencias</em> com o nome do lead e a marca "Caçadores de Ofertas".
                                                                    </p>
                                                                </ListGroup.Item>
                                                                <ListGroup.Item>
                                                                    <strong>2. Botões de resposta</strong>
                                                                    <p className="mb-0 text-muted">
                                                                        • Confirmar Recebimento — ativa interesse, remove da blacklist e dispara promoções.
                                                                    </p>
                                                                    <p className="mb-0 text-muted">
                                                                        • Encerrar Notificacoes — adiciona na blacklist e interrompe comunicações.
                                                                    </p>
                                                                    <p className="mb-0 text-muted">
                                                                        • Ajustar Preferencias  — mantém o lead em jornada manual.
                                                                    </p>
                                                                </ListGroup.Item>
                                                                <ListGroup.Item>
                                                                    <strong>3. Promoções direcionadas</strong>
                                                                    <p className="mb-0 text-muted">
                                                                        Envia automaticamente todas as promoções vinculadas ao lead dentro da janela de 24h.
                                                                    </p>
                                                                </ListGroup.Item>
                                                            </ListGroup>
                                                        </Card.Body>
                                                    </Card>
                                                </Col>
                                                <Col lg={6} className="mb-3">
                                                    {isAdmin ? (
                                                        <Card className="h-100">
                                                            <Card.Body>
                                                                <Card.Title>Disparos rápidos</Card.Title>
                                                                <Card.Subtitle className="text-muted mb-3">
                                                                    Mantenha o funil aquecido com os principais gatilhos.
                                                                </Card.Subtitle>
                                                                <div className="d-flex flex-column">
                                                                    <Button
                                                                        className="mb-2"
                                                                        onClick={handleBulkSendTemplate}
                                                                        disabled={selectedCount === 0 || bulkTemplateSending}
                                                                    >
                                                                        {bulkTemplateSending
                                                                            ? "Enviando templates..."
                                                                            : "Enviar template para selecionados"}
                                                                    </Button>
                                                                    <Button
                                                                        variant="outline-primary"
                                                                        className="mb-2"
                                                                        onClick={() =>
                                                                            handleAction(
                                                                                "promocoes-interesse",
                                                                                enviarPromocoesParaLeadsInteressados
                                                                            )
                                                                        }
                                                                        disabled={pendingAction === "promocoes-interesse"}
                                                                    >
                                                                        {pendingAction === "promocoes-interesse"
                                                                            ? "Disparando promoções..."
                                                                            : "Promoções para interessados"}
                                                                    </Button>
                                                                </div>
                                                                <Alert variant="light" className="mt-3 mb-0">
                                                                    As promoções são enviadas apenas para leads com interesse confirmado, dentro da janela de 24 horas e com promoções vinculadas.
                                                                </Alert>
                                                            </Card.Body>
                                                        </Card>
                                                    ) : (
                                                        <Card className="h-100">
                                                            <Card.Body>
                                                                <Card.Title>Disparos rápidos</Card.Title>
                                                                <Card.Subtitle className="text-muted mb-3">
                                                                    Apenas administradores podem realizar disparos automáticos.
                                                                </Card.Subtitle>
                                                                <Alert variant="light" className="mb-0">
                                                                    Utilize as listas de interesse para informar um administrador quando for necessário enviar templates ou promoções.
                                                                </Alert>
                                                            </Card.Body>
                                                        </Card>
                                                    )}
                                                </Col>
                                            </Row>
                                        </div>
                                    </Tab>

                                    <Tab eventKey="operations" title="Operações">
                                        <div className="pt-4">
                                            <Card className="mb-3">
                                                <Card.Body>
                                                    <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center mb-3">
                                                        <div>
                                                            <Card.Title className="mb-1">Segmentação por interesse</Card.Title>
                                                            <Card.Subtitle className="text-muted">
                                                                Use filtros para selecionar leads por categoria, produto ou status antes de disparar.
                                                            </Card.Subtitle>
                                                        </div>
                                                        {selectedCount > 0 && (
                                                            <Button variant="link" className="p-0" onClick={handleClearSelection}>
                                                                Limpar seleção ({selectedCount})
                                                            </Button>
                                                        )}
                                                    </div>
                                                    <Row className="mb-3">
                                                        <Col md={4} className="mb-2">
                                                            <Form.Label className="mb-1">Buscar</Form.Label>
                                                            <Form.Control
                                                                value={search}
                                                                onChange={(event) => setSearch(event.target.value)}
                                                                placeholder="Nome ou telefone"
                                                            />
                                                        </Col>
                                                        <Col md={4} className="mb-2">
                                                            <Form.Label className="mb-1">Status</Form.Label>
                                                            <Form.Control
                                                                as="select"
                                                                value={statusFilter}
                                                                onChange={(event) => setStatusFilter(event.target.value)}
                                                            >
                                                                <option value="all">Todos</option>
                                                                <option value="interested">Interessados</option>
                                                                <option value="onboarding">Onboarding</option>
                                                                <option value="blacklist">Blacklist</option>
                                                            </Form.Control>
                                                        </Col>
                                                        <Col md={4} className="mb-2">
                                                            <Form.Label className="mb-1">Categoria</Form.Label>
                                                            <Form.Control
                                                                as="select"
                                                                value={categoryFilter}
                                                                onChange={(event) => setCategoryFilter(event.target.value)}
                                                            >
                                                                <option value="all">Todas</option>
                                                                {categoryOptions.map((category) => (
                                                                    <option key={category} value={category}>
                                                                        {category}
                                                                    </option>
                                                                ))}
                                                            </Form.Control>
                                                        </Col>
                                                    </Row>
                                                    <Row className="mb-3">
                                                        <Col md={4} className="mb-2">
                                                            <Form.Label className="mb-1">Produto</Form.Label>
                                                            <Form.Control
                                                                as="select"
                                                                value={productFilter}
                                                                onChange={(event) => setProductFilter(event.target.value)}
                                                            >
                                                                <option value="all">Todos</option>
                                                                {productOptions.map((product) => (
                                                                    <option key={product} value={product}>
                                                                        {product}
                                                                    </option>
                                                                ))}
                                                            </Form.Control>
                                                        </Col>
                                                        <Col md={3} className="mb-2">
                                                            <Form.Label className="mb-1">Lote por job (50–5000)</Form.Label>
                                                            <Form.Control
                                                                type="number"
                                                                min={50}
                                                                max={5000}
                                                                value={templateBatchSize}
                                                                onChange={(event) => setTemplateBatchSize(event.target.value)}
                                                            />
                                                            <Form.Text className="text-muted">
                                                                Lotes maiores (ex.: 1000–2000) reduzem o número de jobs para 4k–100k leads.
                                                            </Form.Text>
                                                        </Col>
                                                        <Col md={5} className="mb-2 d-flex align-items-end">
                                                            <div className="d-flex flex-wrap">
                                                                <Button
                                                                    variant="outline-primary"
                                                                    className="mr-2 mb-2"
                                                                    disabled={!isAdmin || selectedCount === 0 || bulkTemplateSending}
                                                                    onClick={handleBulkSendTemplate}
                                                                >
                                                                    {bulkTemplateSending ? "Enviando templates..." : "Enviar template"}
                                                                </Button>
                                                                <Button
                                                                    variant="outline-secondary"
                                                                    className="mr-2 mb-2"
                                                                    disabled={!isAdmin || eligibleSelectedCount === 0}
                                                                    onClick={handleOpenBulkCustomMessageModal}
                                                                >
                                                                    Mensagem personalizada
                                                                </Button>
                                                                <Button
                                                                    variant="outline-success"
                                                                    className="mb-2"
                                                                    disabled={!isAdmin || selectedCount === 0 || bulkPromotionSending}
                                                                    onClick={handleBulkSendPromotions}
                                                                >
                                                                    {bulkPromotionSending ? "Disparando promoções..." : "Disparar promoções"}
                                                                </Button>
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                    <div className="table-responsive">
                                                        <Table hover responsive>
                                                            <thead>
                                                                <tr>
                                                                    <th style={{ width: 48 }}>
                                                                        <Form.Check
                                                                            type="checkbox"
                                                                            id="automation-select-all-leads"
                                                                            ref={selectAllRef}
                                                                            checked={allFilteredSelected}
                                                                            onChange={handleToggleSelectAll}
                                                                            disabled={filteredLeadIdSet.size === 0}
                                                                            aria-label="Selecionar todos os leads listados"
                                                                        />
                                                                    </th>
                                                                    <th>Lead</th>
                                                                    <th>Status</th>
                                                                    <th>Preferências</th>
                                                                    <th>Última interação</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {filteredLeads.length === 0 && (
                                                                    <tr>
                                                                        <td colSpan={5} className="text-center text-muted py-4">
                                                                            Nenhum lead encontrado com os filtros selecionados.
                                                                        </td>
                                                                    </tr>
                                                                )}
                                                                {filteredLeads.map((lead) => {
                                                                    const categories = Array.isArray(lead.categories)
                                                                        ? lead.categories
                                                                        : Array.isArray(lead.categorias)
                                                                            ? lead.categorias
                                                                            : [];
                                                                    const products = Array.isArray(lead.products)
                                                                        ? lead.products
                                                                        : Array.isArray(lead.produtos)
                                                                            ? lead.produtos
                                                                            : [];

                                                                    return (
                                                                        <tr key={lead.id}>
                                                                            <td className="align-middle" style={{ width: 48 }}>
                                                                                <Form.Check
                                                                                    type="checkbox"
                                                                                    id={`automation-lead-select-${lead.id}`}
                                                                                    checked={selectedLeadIds.includes(lead.id)}
                                                                                    onChange={() => toggleLeadSelection(lead.id)}
                                                                                    aria-label={`Selecionar ${lead.name || "lead"}`}
                                                                                />
                                                                            </td>
                                                                            <td className="align-middle">
                                                                                <strong>{lead.name}</strong>
                                                                                <div className="text-muted">{lead.phone}</div>
                                                                                {isAdmin && (
                                                                                    <div>
                                                                                        <Button
                                                                                            variant="link"
                                                                                            className="p-0 mt-1"
                                                                                            disabled={lead.blacklist}
                                                                                            title={
                                                                                                lead.blacklist
                                                                                                    ? "Lead na blacklist. Remova-o para enviar mensagens."
                                                                                                    : undefined
                                                                                            }
                                                                                            onClick={() => openCustomMessageModalForLead(lead)}
                                                                                        >
                                                                                            Mensagem personalizada
                                                                                        </Button>
                                                                                    </div>
                                                                                )}
                                                                            </td>
                                                                            <td className="align-middle">
                                                                                <div className="d-flex flex-column">
                                                                                    <Badge variant={lead.interesse ? "success" : "secondary"}>
                                                                                        {lead.interesse ? "Interessado" : "Sem interesse"}
                                                                                    </Badge>
                                                                                    <Badge
                                                                                        variant={lead.blacklist ? "danger" : "light"}
                                                                                        className="mt-1"
                                                                                    >
                                                                                        {lead.blacklist ? "Blacklist" : "Liberado"}
                                                                                    </Badge>
                                                                                </div>
                                                                            </td>
                                                                            <td className="align-middle" style={{ minWidth: 220 }}>
                                                                                {categories.length === 0 && products.length === 0 ? (
                                                                                    <span className="text-muted">Sem preferências registradas</span>
                                                                                ) : (
                                                                                    <div>
                                                                                        {categories.length > 0 && (
                                                                                            <div className="mb-2">
                                                                                                <small className="text-muted text-uppercase d-block">
                                                                                                    Categorias
                                                                                                </small>
                                                                                                <div className="d-flex flex-wrap">
                                                                                                    {categories.map((category) => (
                                                                                                        <Badge
                                                                                                            key={`${lead.id}-category-${category}`}
                                                                                                            variant="primary"
                                                                                                            className="mr-1 mb-1"
                                                                                                        >
                                                                                                            {category}
                                                                                                        </Badge>
                                                                                                    ))}
                                                                                                </div>
                                                                                            </div>
                                                                                        )}
                                                                                        {products.length > 0 && (
                                                                                            <div>
                                                                                                <small className="text-muted text-uppercase d-block">
                                                                                                    Produtos
                                                                                                </small>
                                                                                                <div className="d-flex flex-wrap">
                                                                                                    {products.map((product) => (
                                                                                                        <Badge
                                                                                                            key={`${lead.id}-product-${product}`}
                                                                                                            variant="info"
                                                                                                            className="mr-1 mb-1"
                                                                                                        >
                                                                                                            {product}
                                                                                                        </Badge>
                                                                                                    ))}
                                                                                                </div>
                                                                                            </div>
                                                                                        )}
                                                                                    </div>
                                                                                )}
                                                                            </td>
                                                                            <td className="align-middle">
                                                                                {formatDateTime(lead.last_interaction_at)}
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                            </tbody>
                                                        </Table>
                                                    </div>
                                                </Card.Body>
                                            </Card>

                                            <Row className="mt-3">
                                                <Col lg={6} className="mb-3">
                                                    {isAdmin ? (
                                                        <Card className="h-100">
                                                            <Card.Body>
                                                                <Card.Title>Envio direcionado</Card.Title>
                                                                <Card.Subtitle className="text-muted mb-3">
                                                                    Personalize o disparo de promoções para um lead específico.
                                                                </Card.Subtitle>
                                                                <Form>
                                                                    <Form.Group controlId="automationLeadSelect">
                                                                        <Form.Label>Selecione o lead</Form.Label>
                                                                        <Form.Control
                                                                            as="select"
                                                                            value={selectedLead}
                                                                            onChange={(event) => setSelectedLead(event.target.value)}
                                                                        >
                                                                            <option value="">Escolha um lead</option>
                                                                            {leads.map((lead) => (
                                                                                <option key={lead.id} value={lead.id}>
                                                                                    {lead.name}
                                                                                </option>
                                                                            ))}
                                                                        </Form.Control>
                                                                    </Form.Group>
                                                                    <Button
                                                                        className="mt-3"
                                                                        disabled={!selectedLead || pendingAction === "promocoes-lead"}
                                                                        onClick={() =>
                                                                            handleAction("promocoes-lead", () =>
                                                                                enviarPromocoesParaLead(Number(selectedLead))
                                                                            )
                                                                        }
                                                                    >
                                                                        {pendingAction === "promocoes-lead"
                                                                            ? "Disparando..."
                                                                            : "Enviar promoções deste lead"}
                                                                    </Button>
                                                                    <Button
                                                                        variant="outline-secondary"
                                                                        className="mt-3 ml-2"
                                                                        disabled={!selectedLeadData || selectedLeadData.blacklist}
                                                                        onClick={() =>
                                                                            selectedLeadData &&
                                                                            openCustomMessageModalForLead(selectedLeadData)
                                                                        }
                                                                    >
                                                                        Mensagem personalizada
                                                                    </Button>
                                                                </Form>

                                                                {selectedLeadData && (
                                                                    <Alert variant="light" className="mt-3 mb-0">
                                                                        <h6 className="mb-1">Contexto do lead</h6>
                                                                        <p className="mb-1">
                                                                            Última interação: {formatRelativeDate(selectedLeadData.last_interaction_at)}
                                                                        </p>
                                                                        <p className="mb-1">
                                                                            Interesse: {selectedLeadData.interesse ? "Confirmado" : "Pendente"}
                                                                            {selectedLeadData.blacklist && " • Blacklist"}
                                                                        </p>
                                                                        <p className="mb-0">
                                                                            Promoções vinculadas:
                                                                            {(selectedLeadData.promocoes || []).length > 0 ? (
                                                                                (selectedLeadData.promocoes || []).map((promo) => (
                                                                                    <Badge key={promo.id} variant="info" className="ml-1">
                                                                                        {promo.nome}
                                                                                    </Badge>
                                                                                ))
                                                                            ) : (
                                                                                <span className="ml-1 text-muted">Nenhuma promoção vinculada</span>
                                                                            )}
                                                                        </p>
                                                                    </Alert>
                                                                )}
                                                            </Card.Body>
                                                        </Card>
                                                    ) : (
                                                        <Card className="h-100">
                                                            <Card.Body>
                                                                <Card.Title>Envio direcionado</Card.Title>
                                                                <Card.Subtitle className="text-muted mb-3">
                                                                    Somente administradores podem enviar promoções diretamente do painel.
                                                                </Card.Subtitle>
                                                                <Alert variant="light" className="mb-0">
                                                                    Compartilhe as demandas identificadas com a equipe administrativa para que os disparos sejam realizados com segurança.
                                                                </Alert>
                                                            </Card.Body>
                                                        </Card>
                                                    )}
                                                </Col>
                                                <Col lg={6} className="mb-3">
                                                    <Card className="h-100">
                                                        <Card.Body>
                                                            <Card.Title>Promoções cadastradas</Card.Title>
                                                            <Card.Subtitle className="text-muted mb-3">
                                                                Visão rápida das campanhas que fazem parte da automação.
                                                            </Card.Subtitle>
                                                            {promotions.length === 0 ? (
                                                                <Alert variant="light" className="mb-0">
                                                                    Cadastre promoções para começar a nutrir os leads após a confirmação de interesse.
                                                                </Alert>
                                                            ) : (
                                                                <ListGroup variant="flush" className="promotion-preview">
                                                                    {promotions.map((promotion) => (
                                                                        <ListGroup.Item key={promotion.id}>
                                                                            <div className="d-flex justify-content-between align-items-start">
                                                                                <div>
                                                                                    <strong>{promotion.nome}</strong>
                                                                                    <p className="mb-1 text-muted">{promotion.link}</p>
                                                                                </div>
                                                                                <Badge variant="light">
                                                                                    {(promotion.leads || []).length} leads
                                                                                </Badge>
                                                                            </div>
                                                                        </ListGroup.Item>
                                                                    ))}
                                                                </ListGroup>
                                                            )}
                                                        </Card.Body>
                                                    </Card>
                                                </Col>
                                            </Row>
                                        </div>
                                    </Tab>

                                    <Tab eventKey="interactions" title="Interações">
                                        <div className="pt-4">
                                            <Card>
                                                <Card.Body className="table-responsive">
                                                    <Card.Title className="mb-3">Últimas solicitações de produtos</Card.Title>
                                                    {recentRequests.length === 0 ? (
                                                        <Alert variant="light" className="mb-0">
                                                            Nenhuma solicitação registrada até o momento.
                                                        </Alert>
                                                    ) : (
                                                        <Table hover responsive>
                                                            <thead>
                                                                <tr>
                                                                    <th>Lead</th>
                                                                    <th>Produto</th>
                                                                    <th>Categoria</th>
                                                                    <th>Solicitado em</th>
                                                                    <th className="text-right">Ações</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {recentRequests.map((request) => (
                                                                    <tr key={request.id}>
                                                                        <td className="align-middle" style={{ minWidth: 200 }}>
                                                                            <strong>{request.lead?.name || "Lead"}</strong>
                                                                            <div className="text-muted">{request.lead?.phone}</div>
                                                                        </td>
                                                                        <td className="align-middle" style={{ maxWidth: 260 }}>
                                                                            {request.productName || "-"}
                                                                        </td>
                                                                        <td className="align-middle">
                                                                            <Badge variant="info">{request.category}</Badge>
                                                                        </td>
                                                                        <td className="align-middle">
                                                                            {formatDateTime(request.createdAt)}
                                                                        </td>
                                                                        <td className="align-middle text-right">
                                                                            <Button
                                                                                variant="link"
                                                                                className="p-0"
                                                                                onClick={() => setSelectedRequest(request)}
                                                                            >
                                                                                Ver detalhes
                                                                            </Button>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </Table>
                                                    )}
                                                </Card.Body>
                                            </Card>

                                            {logs.length > 0 && (
                                                <Card className="mt-3">
                                                    <Card.Body>
                                                        <Card.Title>Histórico de execuções</Card.Title>
                                                        <ListGroup variant="flush">
                                                            {logs.map((log) => (
                                                                <ListGroup.Item key={log.id}>
                                                                    <Badge variant={log.variant} className="mr-2">
                                                                        {log.variant === "danger" ? "Erro" : "Sucesso"}
                                                                    </Badge>
                                                                    {log.message}
                                                                </ListGroup.Item>
                                                            ))}
                                                        </ListGroup>
                                                    </Card.Body>
                                                </Card>
                                            )}
                                        </div>
                                    </Tab>
                                </Tabs>

                            </>
                        )}
                    </div>
                </div>
            </Container>
            <CustomMessageModal
                show={customMessageModal.open}
                onHide={handleCloseCustomMessageModal}
                onSubmit={handleSubmitCustomMessage}
                sending={customMessageSending}
                leadCount={customMessageModal.leadIds.length}
                targetLabel={customMessageModal.contextLabel}
            />
            <Modal
                show={Boolean(selectedRequest)}
                onHide={() => setSelectedRequest(null)}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Solicitação de produto</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedRequest && (
                        <>
                            <h5 className="mb-1">{selectedRequest.lead?.name || "Lead"}</h5>
                            <p className="text-muted mb-3">{selectedRequest.lead?.phone}</p>
                            <div className="mb-3">
                                <strong className="d-block">Produto desejado</strong>
                                <span>{selectedRequest.productName || "Não informado"}</span>
                            </div>
                            <div className="mb-3">
                                <strong className="d-block">Categoria sugerida</strong>
                                <Badge variant="info">{selectedRequest.category}</Badge>
                            </div>
                            <div>
                                <strong className="d-block">Solicitado em</strong>
                                <span>
                                    {formatDateTime(selectedRequest.createdAt)} ({
                                        selectedRequest.createdAt
                                            ? formatRelativeDate(selectedRequest.createdAt)
                                            : "tempo indeterminado"
                                    })
                                </span>
                            </div>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={() => setSelectedRequest(null)}>
                        Fechar
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}